import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import useIgnorableError from '@ecp/common/src/hooks/useIgnorableError';
import { overrideProperties } from '@ecp/common/src/style/utils';
import { fontPropTypes, sizePropTypes } from '@ecp/common/src/style/propTypes';
import boTheme from '@ecp/common/src/style/theme/bo';
import { textEllipsis } from '@ecp/common/src/style/theme/defaultStyled';
import { FlexBox, FlexBoxColumn, FlexBoxSpaceBetween, ImageBox } from '@ecp/common/src/layouts/flex/styled';
import { TextBox } from '@ecp/common/src/text/TextBox';
import DropdownUnstyled, { stateClassNames } from '@ecp/common/src/components/unstyled/dropdown/DropdownUnstyled';
import { DropdownItemsRoot } from '@ecp/common/src/components/dropdown/Dropdown';
import Tooltip from '@ecp/common/src/components/tooltip/bo/Tooltip';
import { flatBottom, flatTop } from '@ecp/common/src/components/dropdown/bo/DropdownMenu';
import { ReactComponent as CollapseArrow } from '@ecp/common/src/assets/icon/collapse_arrow.svg';
import HelpIcon from '@ecp/common/src/assets/icon/icon__tooltip-question.svg';

const textDefaultProps = {
  'font-size': boTheme.font.size.small,
  'font-weight': boTheme.font.weight.regular.toString(),
};

const StyledHelpIcon = styled.div`
  width: 18px;
  height: 18px;
  cursor: pointer;
  background-image: url(${HelpIcon});
`;

const StyledErrorMessage = styled.div`
  height: 13px;
  font-weight: ${boTheme.font.weight.regular};
  font-size: ${boTheme.font.size.small};
  line-height: 13px;

  color: ${boTheme.color.error1};
`;

const DefaultLabelRoot = styled(FlexBoxSpaceBetween)`
  align-items: center;
  width: 100%;
  height: 28px;
  padding: 0 8px;
  border: 1px ${boTheme.color.sub6} solid;
  border-radius: 3px;
  ${overrideProperties(fontPropTypes, textDefaultProps)};
  background-color: ${boTheme.color.sub10};
  cursor: pointer;

  &.${stateClassNames.disabled} {
    background-color: ${boTheme.color.sub8};
  }
  &.${stateClassNames.readOnly} {
    background-color: ${boTheme.color.sub8};
  }
  &.${stateClassNames.open} {
    border-color: ${boTheme.color.main};

    ${({ isFlip }) => (isFlip ? flatTop : flatBottom)}
  }
  &.${stateClassNames.error} {
    border-color: ${boTheme.color.error1};
  }
`;

const DefaultLabelText = styled(TextBox)`
  line-height: ${boTheme.font.lineHeight.regular};
  ${overrideProperties(fontPropTypes, textDefaultProps)};
  width: ${({ width = '100%' }) => width};
  color: ${boTheme.color.sub1};
  &.${stateClassNames.notSelected} {
    color: ${boTheme.color.sub4};
  }
`;

const DefaultLabelIcon = styled(CollapseArrow)`
  width: 10px;
  height: 6px;
  & path {
    fill: ${boTheme.color.sub2};
  }

  &.${stateClassNames.disabled} {
    & path {
      fill: ${boTheme.color.sub6};
    }
  }

  &.${stateClassNames.open} {
    transform: rotate(180deg);
  }
`;

const DefaultItemWrapper = styled(FlexBox)`
  align-items: center;
  height: 28px;
  line-height: 28px;
  padding: 0 8px;
  gap: 4px;

  &:hover {
    background-color: ${boTheme.color.table4};
  }
`;

const DefaultItem = styled.div`
  ${textEllipsis};
  line-height: ${boTheme.font.lineHeight.regular};

  ${overrideProperties(fontPropTypes, textDefaultProps)};
  color: ${({ selected }) => (selected ? boTheme.color.main : boTheme.color.sub1)};
`;

export const StyledDefaultLabel = ({ item, placeholder, className, isFlip, width = '100%' }) => {
  const labelWidth = useMemo(() => `calc(${width} - ${item?.url ? 46 : 28}px)`, [item?.url, width]);
  return (
    <DefaultLabelRoot className={className} isFlip={isFlip}>
      <FlexBox gap={'4px'}>
        {item?.url && <ImageBox imageSrc={item.url} width={'14px'} height={'15px'} />}
        <DefaultLabelText ellipsis className={className} width={labelWidth}>
          {item?.label || placeholder}
        </DefaultLabelText>
      </FlexBox>
      <DefaultLabelIcon className={className} />
    </DefaultLabelRoot>
  );
};
StyledDefaultLabel.displayName = 'StyledDefaultLabel';

StyledDefaultLabel.propTypes = {
  item: PropTypes.object,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  isFlip: PropTypes.bool,
  width: PropTypes.string,
};

export const StyledDefaultItem = ({ id, label, url, selectedItems }) => (
  <DefaultItemWrapper>
    {url && <ImageBox imageSrc={url} width={'14px'} height={'15px'} />}
    <DefaultItem selected={selectedItems?.some(({ key }) => id === key)}>{label}</DefaultItem>
  </DefaultItemWrapper>
);

StyledDefaultItem.propTypes = {
  label: PropTypes.string,
  url: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedItems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
};

const SubLabelItemWrapper = styled(DefaultItem)`
  height: 47px;
  flex-direction: column;
  align-items: flex-start;
  padding: 7px 8px;

  &:hover {
    background-color: ${boTheme.color.table4};
  }
`;

const LabelWrapper = styled(FlexBox)`
  align-items: center;
  gap: 4px;
`;

const Label = styled.div`
  height: 17px;
  line-height: ${boTheme.font.lineHeight.regular};
  color: ${boTheme.color.sub1};
`;

const SubLabel = styled.div`
  height: 16px;
  line-height: 16px;
  color: ${boTheme.color.sub12};
`;
export const DropdownSubLabelItem = ({ id, label, subLabel, url, selectedItems }) => (
  <SubLabelItemWrapper selected={selectedItems.some(({ key }) => id === key)}>
    <LabelWrapper>
      {url && <ImageBox imageSrc={url} width={'14px'} height={'15px'} />}
      <Label>{label}</Label>
    </LabelWrapper>
    <SubLabel>{subLabel}</SubLabel>
  </SubLabelItemWrapper>
);

DropdownSubLabelItem.propTypes = {
  label: PropTypes.string,
  subLabel: PropTypes.string,
  url: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedItems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
};

export const StyledDropdownRoot = styled.div`
  position: relative;
  ${overrideProperties(sizePropTypes, { width: '100%', height: 'auto' })};
`;

const flatTopItem = css`
  border-bottom: none;
  border-radius: 3px 3px 0 0;
`;

const flatBottomItem = css`
  border-top: none;
  border-radius: 0 0 3px 3px;
`;

const StyledDropdownItemsRoot = styled(DropdownItemsRoot)`
  border-radius: 3px;
  border-color: ${boTheme.color.main};
  ${({ isFlip }) => (isFlip ? flatTopItem : flatBottomItem)}
`;

const Dropdown = React.forwardRef(({ help, helpValue, value, error, disableIgnoringError, ...props }, ref) => {
  const { errorMessage } = props;

  const { ignoreError } = useIgnorableError({ error, value, disableIgnoringError });

  return (
    <FlexBoxColumn align-items={'flex-start'} gap={'6px'}>
      <FlexBox gap={'6px'}>
        <DropdownUnstyled
          ref={ref}
          dropdownRoot={StyledDropdownRoot}
          dropdownItemsRoot={StyledDropdownItemsRoot}
          value={value}
          error={!ignoreError && error}
          {...props}
        />
        {help && (
          <Tooltip arrow openWhenHovered placement="right" value={helpValue}>
            <StyledHelpIcon />
          </Tooltip>
        )}
      </FlexBox>
      {!ignoreError && error && errorMessage && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
    </FlexBoxColumn>
  );
});
Dropdown.displayName = 'Dropdown';

Dropdown.propTypes = {
  ...sizePropTypes,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string, PropTypes.number]),
  labelSlot: PropTypes.elementType,
  itemSlot: PropTypes.elementType,
  items: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  itemsMaxHeight: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  help: PropTypes.bool,
  helpValue: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element, PropTypes.node]),
  disableIgnoringError: PropTypes.bool,
};

Dropdown.defaultProps = {
  labelSlot: StyledDefaultLabel,
  itemSlot: StyledDefaultItem,
  items: [],
  placeholder: '선택하세요',
  disabled: false,
  placement: 'bottom-start',
  help: false,
};

export default Dropdown;
